import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search'
import { TableRow, TableCell } from '@mui/material'
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import ClearIcon from "@mui/icons-material/Clear";
import Chart from "../../../components/Chart";
import ChartButton from '../../../components/Button/ChartButton';
import TableComponent from '../../../components/Table/TableComponent';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import { BMI, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const BMIForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [bmiRecords, setBMIRecords] = useState<BMI[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [openBMIDialog, setOpenBMIDialog] = useState(false)
  const [editBMIRecord, setEditBMIRecord] = useState<BMI | null>(null)
  const [showChart, setShowChart] = useState(false)
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success')
 
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState<BMI | null>(null)
  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  useEffect(() => {
    fetchBMIRecords(dateRange.startDate, dateRange.endDate)
  }, [page, rowsPerPage, searchTerm]) // Add startDate and endDate as dependencies

  const fetchBMIRecords = async (startDate: string = '', endDate: string = '') => {
    setBMIRecords([]);
    setLoading(true);
    const authToken = sessionStorage.getItem('authToken');
    try {
      const response = await axios.get<ObjectiveResponse>(
        `${BASE_URL}/resource/vitals`,
        {
          params: {
            limit: rowsPerPage,
            page: page + 1,
            from: startDate || '',   // Add start date for filtering
            to: endDate || '',       // Add end date for filtering
            order_by: 'details->date',
            dir: 2,
            user_id: patientId,
            slug: 'bmi',
            searchkey: searchTerm || undefined,
          },
          headers: getAxiosConfig().headers
        }
      );
  
      const { vitals } = response.data.data;
      const total = response.data.data.pagination?.total || 0;
      
      if (Array.isArray(vitals) && vitals.length > 0) {
        const formattedData: BMI[] = vitals.map((vital) => ({
          id: vital.id,
          user_id: vital.user_id, // Add this field
          consult_id: vital.consult_id, // Add this field
          peripheral_id: vital.peripheral_id, // Add this field
          slug: vital.slug, // Add this field
          details: { // Add the 'details' object structure
            bmi: vital.details.bmi,
            date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
            time: vital.details.time,
            height: vital.details.height,
            weight: vital.details.weight,
            bmiFlag: vital.details.bmiFlag,
            range_code: vital.details.range_code,
            height_unit: vital.details.height_unit,
            weight_unit: vital.details.weight_unit,
            bmiFlagColor: vital.details.bmiFlagColor || '',
          },
          freeze: vital.freeze,
          document: vital.document || null // Add 'document' if it exists, otherwise set as null
        }));
        
        setBMIRecords(formattedData);
        setTotalRecords(total);
      } else {
        console.warn('No vitals found for the given filters.');
      }
  
    } catch (error) {
      console.error('Error fetching BMI records:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setPage(0); // Reset to the first page
    setRowsPerPage(5); // Reset rows per page if needed
    fetchBMIRecords(); // Fetch all records again
    setFiltersApplied(false);                   // Fetch without date filter
  };

  const renderRow = (item: BMI, index: number) => (
    <TableRow key={index}>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}> {page * rowsPerPage + index + 1}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem'}}>{(item.details.date)}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem'}}>{item.details.time}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}>{item.details.height} cm</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem'}}>{item.details.weight} kg</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}>{item.details.bmi} kg/m²</TableCell>
      <TableCell sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}>
        <span
          style={{
            backgroundColor: item.details.range_code,
            padding: '4px 8px',
            borderRadius: '8px',
            color: '#FFFFFF'
          }}
        >
          {item.details.bmiFlag}
        </span>
      </TableCell>
    </TableRow>
  )

  const chartData = {
    labels: bmiRecords.map((record) => record.details.date),
    datasets: [
      {
        label: 'BMI (kg/m²)',
        data: bmiRecords.map((record) =>
          typeof record.details.bmi === 'number' ? record.details.bmi : 0),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderWidth: 1
      },
    ]
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw} kg/m²`
        },
      },
    },
  }

  return (
    <div>
      <div className="flex items-end justify-between w-full gap-12">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId="BMI_Start_Date"
            endDateId="BMI_End_Date"
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates);
              setFiltersApplied(true);
            }}
            value={dateRange} // Pass the selected date range
          />
          {/* Added gap and centered items */}
              <IconButton
                icon={<SearchIcon />}
                onClick={() => {
                  fetchBMIRecords(dateRange.startDate, dateRange.endDate);
                  setFiltersApplied(true); // Mark filters as applied when search is clicked
                }}
              />

              {filtersApplied && (
                <IconButton
                  icon={<ClearIcon />}
                  onClick={handleClearFilters}
                />
              )}
           
        </div>
        <div className="flex space-x-4">
    
          <ChartButton
            text={showChart ? 'List' : 'Chart'}
            onClick={() => setShowChart((prev) => !prev)}
          />
         </div>
      </div>
      {loading ? (
      <div className="text-center my-4">
         
          </div>
      ) : (
        <>
          {showChart ? (
            <Chart data={chartData} options={chartOptions} />
          ) : (
            <div>
          <TableComponent
            headers={[
              'S.No',
              'Date',
              'Time',
              'Height (cm)',
              'Weight (kg)',
              'BMI (kg/m²)',
              'Result',
              
            ]}
            data={bmiRecords}
            renderRow={renderRow}
            loading={loading} error={''} />
             
            </div>
          )}
        </>
      )}
        <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
    </div>
  )
}

export default BMIForm
